// ---------------------------------------------------------------------------
// Modo embutido: o painel aberto dentro do PWA do SGA Desk.
//
// Aqui o painel não é um aplicativo — é uma aba do SGA Desk. Quem já está
// desenhado em volta não deve ser desenhado de novo: usuário, busca, sino,
// tema e sair vivem no cabeçalho deles, e repetir tudo isso rouba ~56 px de
// altura para mostrar controles que o gestor já tem logo acima.
//
// O tenant é caso à parte. Ele não some — some o **seletor**. O SSO já fixou a
// empresa no token; oferecer uma lista para trocar mostra um controle que não
// controla nada, e o gestor que clica e não vê efeito conclui que o painel está
// quebrado.
// ---------------------------------------------------------------------------

export type Tema = 'light' | 'dark';

/**
 * Estamos dentro de um iframe?
 *
 * Dois sinais, e os dois importam:
 *
 * - `window.self !== window.top` pega o iframe sozinho, sem o SGA Desk
 *   precisar cooperar. Em documento de origem diferente a leitura de `top`
 *   pode lançar — daí o `try`, e a exceção **é** a resposta: se não dá para
 *   olhar para fora, estamos dentro de algo.
 * - `?embed=1` deixa explícito, e serve para o caminho contrário: abrir o
 *   painel numa aba própria a partir do Desk e ainda assim querer o chrome
 *   completo (basta não mandar o parâmetro).
 */
export function estaEmbutido(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get('embed') === '1') return true;
  if (params.get('embed') === '0') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Tema pedido pelo SGA Desk, se ele pediu algum.
 *
 * Lido **na carga do módulo**, não na chamada: o SSO entrega os parâmetros no
 * fragmento (`#/sso?token=…&tema=dark`) e o `auth.tsx` limpa a URL logo depois
 * de ler o token, para ele não ficar na barra de endereço. Quem perguntasse
 * mais tarde acharia a URL já limpa.
 *
 * Procura nos dois lugares: o fragmento é o caminho do SSO, e a query serve
 * para abrir o painel direto com um tema fixo.
 */
const temaInicial: Tema | null = (() => {
  const daQuery = new URLSearchParams(window.location.search).get('tema');
  const hash = window.location.hash;
  const daHash = hash.includes('?')
    ? new URLSearchParams(hash.slice(hash.indexOf('?') + 1)).get('tema')
    : null;
  const t = daQuery ?? daHash;
  return t === 'dark' || t === 'light' ? t : null;
})();

export function temaDaUrl(): Tema | null {
  return temaInicial;
}

/**
 * Segue o tema do SGA Desk enquanto o painel estiver aberto.
 *
 * O parâmetro da URL só vale na abertura; se o gestor troca o tema no Desk com
 * o painel já aberto, o iframe ficaria no tema antigo até recarregar.
 *
 * Duas fontes, nesta ordem:
 *
 * 1. `postMessage` do pai — `{ tipo: 'sgatalk:tema', tema: 'dark' }`. É o
 *    caminho bom, mas depende de o SGA Desk mandar.
 * 2. `prefers-color-scheme` — funciona sem cooperação nenhuma e cobre o caso
 *    de o Desk seguir o tema do sistema operacional.
 *
 * A origem da mensagem **é conferida**: sem isso qualquer página que
 * embutisse o painel poderia mexer nele. Só o Desk configurado manda.
 */
export function seguirTemaDoPai(aplicar: (t: Tema) => void): () => void {
  const permitidas = (import.meta.env.VITE_ORIGENS_EMBUTIDAS ?? '')
    .split(',')
    .map((o: string) => o.trim())
    .filter(Boolean);

  const aoReceber = (e: MessageEvent) => {
    if (permitidas.length > 0 && !permitidas.includes(e.origin)) return;
    const dado = e.data;
    if (dado?.tipo !== 'sgatalk:tema') return;
    if (dado.tema === 'dark' || dado.tema === 'light') aplicar(dado.tema);
  };
  window.addEventListener('message', aoReceber);

  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const aoTrocarSistema = (e: MediaQueryListEvent) => aplicar(e.matches ? 'dark' : 'light');
  mq.addEventListener('change', aoTrocarSistema);

  return () => {
    window.removeEventListener('message', aoReceber);
    mq.removeEventListener('change', aoTrocarSistema);
  };
}

const CHAVE_TEMA = 'sgatalk:tema';

/**
 * `auto` = seguir o SGA Desk (ou, na falta de sinal dele, o sistema).
 *
 * É o padrão, e é o que o gestor espera: ele troca o tema no Desk e o painel
 * acompanha. A escolha manual existe só para quando o Desk ainda não manda o
 * sinal — e precisa ser **desfazível**, senão quem experimentou o botão uma vez
 * fica preso naquele tema para sempre, com o Desk mandando o contrário.
 */
export type Preferencia = Tema | 'auto';

/**
 * Tema escolhido pela pessoa, se ela já escolheu algum.
 *
 * Embutido, o painel não desenha o próprio cabeçalho — e o botão de tema
 * morava lá. Enquanto o SGA Desk não manda `?tema=` nem `postMessage`, sem
 * isto o gestor ficaria preso no tema claro dentro de um Desk escuro, **sem
 * controle nenhum**: o botão sumiu e o sinal externo não veio.
 *
 * A escolha manual vence o `prefers-color-scheme`, e não o parâmetro do Desk:
 * quando o Desk diz o tema dele, ele está certo por definição.
 */
export function preferenciaSalva(): Preferencia {
  try {
    const t = localStorage.getItem(CHAVE_TEMA);
    return t === 'dark' || t === 'light' ? t : 'auto';
  } catch {
    // Storage bloqueado dentro do iframe (Safari em PWA). Não é erro: só
    // significa que a escolha não sobrevive ao recarregamento.
    return 'auto';
  }
}

export function salvarPreferencia(p: Preferencia): void {
  try {
    if (p === 'auto') localStorage.removeItem(CHAVE_TEMA);
    else localStorage.setItem(CHAVE_TEMA, p);
  } catch {
    /* ver acima */
  }
}

/** Tema do sistema, quando não há nem escolha nem ordem do Desk. */
export function temaDoSistema(): Tema {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
